import { type CSSProperties, useEffect, useId, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';

import { smoothPath } from '../lib/smoothPath';
import { useNow } from '../useNow';
import {
  fmtHour,
  fmtPrecip,
  fmtSnow,
  fmtTemp,
  type HourData,
  type PrecipitationUnit,
  type TemperatureUnit
} from '../weather';

// The temperature band is placed as fractions of the week row, clear of the
// date header. The bottom layers are placed in pixels up from the row's
// bottom edge, because the footer row they sit on (daily note, day weather) is
// a fixed height: the sky ribbon lies just above the footer text, and the
// precipitation bars stand on the ribbon.
const BAND_TOP = 0.42;
const BAND_BOTTOM = 0.8;
const SKY_BOTTOM_PX = 24;
const SKY_HEIGHT_PX = 4;
const PRECIP_BASE_PX = 30;
const PRECIP_MAX_PX = 26;
// An hour with this much precipitation reaches full height. Heights follow
// the square root of the amount, so drizzle stays visible and a downpour
// doesn't dwarf everything else. Fixed rather than per-month, so a bar means
// the same amount wherever it appears.
const PRECIP_FULL_MM = 5;
const PRECIP_MIN_MM = 0.1;
// How close (px) the pointer must come before the tooltip shows, so the
// overlay stays out of the way of events it passes over.
const HOVER_REACH = 14;
const HOURS_IN_WEEK = 7 * 24;

export interface TempScale {
  min: number;
  max: number;
}

interface Hour {
  t: number;
  x: number; // end of the hour, 0..1 across the week row
  temp: number | null;
  y: number | null; // temperature height, 0..1 down the row
  rainMm: number;
  snowMm: number; // water equivalent
  snowCm: number;
  prob: number | null;
  cloud: number | null; // 0..100
  isDay: boolean;
}

interface Props {
  week: Date[];
  hoursByDay: Map<string, HourData[]>;
  scale: TempScale | null;
  showTemp: boolean;
  showPrecip: boolean;
  showSky: boolean;
  temperatureUnit: TemperatureUnit;
  precipitationUnit: PrecipitationUnit;
}

// A meteogram through one week row of the month grid: a sky ribbon (daylight
// and cloud), the hourly temperature line, and hourly precipitation bars.
// Each day cell spans 00:00 at its left edge to 24:00 at its right, so noon
// sits mid-cell and all three run on across day boundaries. Pointer events
// are off; hover is read from the row itself.
export default function WeekMeteogram({
  week,
  hoursByDay,
  scale,
  showTemp,
  showPrecip,
  showSky,
  temperatureUnit,
  precipitationUnit
}: Props) {
  // useId's colons would need escaping inside url(#…).
  const gradientId = `sky${useId().replace(/:/g, '')}`;
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ hour: Hour; y: number } | null>(null);
  const now = useNow();

  const { hours, segments } = useMemo(() => {
    const span = scale ? Math.max(1, scale.max - scale.min) : 1;
    const yOf = (temp: number) =>
      scale ? BAND_TOP + (1 - (temp - scale.min) / span) * (BAND_BOTTOM - BAND_TOP) : 0;
    const hours: Hour[] = [];
    const segs: Hour[][] = [];
    let current: Hour[] = [];
    week.forEach((day, col) => {
      for (const h of hoursByDay.get(format(day, 'yyyy-MM-dd')) ?? []) {
        const at = new Date(h.t * 1000);
        const frac = (at.getHours() + at.getMinutes() / 60) / 24;
        const precip = h.precipitation ?? 0;
        const snowCm = h.snowfall ?? 0;
        // Open-Meteo's rule of thumb: 7 cm of snow melts to 1 cm of water.
        const snowMm = Math.min(precip, (snowCm * 10) / 7);
        const hour: Hour = {
          t: h.t,
          x: (col + frac) / 7,
          temp: h.temperature_2m,
          y: h.temperature_2m == null ? null : yOf(h.temperature_2m),
          rainMm: precip - snowMm,
          snowMm,
          snowCm,
          prob: h.precipitation_probability,
          cloud: h.cloud_cover ?? null,
          isDay: h.is_day !== 0
        };
        hours.push(hour);
        if (hour.y == null) continue;
        // A missing hour breaks the line rather than bridging the gap.
        const prev = current[current.length - 1];
        if (prev && hour.t - prev.t > 3600) {
          segs.push(current);
          current = [];
        }
        current.push(hour);
      }
    });
    if (current.length) segs.push(current);
    return { hours, segments: segs };
  }, [week, hoursByDay, scale]);

  const nowT = now.getTime() / 1000;
  const drawTemp = showTemp && scale !== null && segments.length > 0;
  const bars = useMemo(
    () => (showPrecip ? hours.filter((h) => h.rainMm + h.snowMm >= PRECIP_MIN_MM) : []),
    [hours, showPrecip]
  );

  const skyHours = useMemo(
    () => (showSky ? hours.filter((h): h is Hour & SkyHour => h.cloud != null) : []),
    [hours, showSky]
  );

  // Where "now" falls on the line, interpolated between the hours around it.
  const nowPoint = useMemo(() => {
    if (!drawTemp) return null;
    const line = segments.flat();
    for (let i = 0; i < line.length - 1; i++) {
      const a = line[i];
      const b = line[i + 1];
      if (a.t <= nowT && nowT < b.t && b.t - a.t <= 3600) {
        const f = (nowT - a.t) / (b.t - a.t);
        return { x: a.x + (b.x - a.x) * f, y: a.y! + (b.y! - a.y!) * f };
      }
    }
    return null;
  }, [drawTemp, segments, nowT]);

  useEffect(() => {
    const row = ref.current?.parentElement;
    if (!row || hours.length === 0) return;
    function onMove(ev: MouseEvent) {
      const rect = row!.getBoundingClientRect();
      const fx = (ev.clientX - rect.left) / rect.width;
      const py = (ev.clientY - rect.top) / rect.height;
      const reach = HOVER_REACH / rect.height;
      const fromBottom = rect.bottom - ev.clientY;
      const slot = 1 / HOURS_IN_WEEK;
      // Near the temperature line: the reading closest in time.
      if (drawTemp) {
        let nearest: Hour | null = null;
        for (const h of hours) {
          if (h.y == null) continue;
          if (!nearest || Math.abs(h.x - fx) < Math.abs(nearest.x - fx)) nearest = h;
        }
        if (nearest && Math.abs(nearest.x - fx) <= slot / 2 && Math.abs(py - nearest.y!) <= reach) {
          setHover({ hour: nearest, y: nearest.y! });
          return;
        }
      }
      // On the sky ribbon: the reading closest in time.
      if (
        skyHours.length > 0 &&
        fromBottom >= SKY_BOTTOM_PX - 4 &&
        fromBottom <= SKY_BOTTOM_PX + SKY_HEIGHT_PX + 4
      ) {
        let nearest = skyHours[0];
        for (const h of skyHours) {
          if (Math.abs(h.x - fx) < Math.abs(nearest.x - fx)) nearest = h;
        }
        if (Math.abs(nearest.x - fx) <= slot / 2) {
          setHover({ hour: nearest, y: 1 - (SKY_BOTTOM_PX + SKY_HEIGHT_PX) / rect.height });
          return;
        }
      }
      // Over a precipitation bar: bars cover the hour before their reading.
      if (showPrecip) {
        const bar = bars.find((h) => fx > h.x - slot && fx <= h.x);
        if (bar) {
          const topPx = PRECIP_BASE_PX + barHeight(bar.rainMm + bar.snowMm) * PRECIP_MAX_PX;
          if (fromBottom >= PRECIP_BASE_PX - 2 && fromBottom <= topPx + HOVER_REACH / 2) {
            setHover({ hour: bar, y: 1 - topPx / rect.height });
            return;
          }
        }
      }
      setHover(null);
    }
    function onLeave() {
      setHover(null);
    }
    row.addEventListener('mousemove', onMove);
    row.addEventListener('mouseleave', onLeave);
    return () => {
      row.removeEventListener('mousemove', onMove);
      row.removeEventListener('mouseleave', onLeave);
    };
  }, [hours, bars, skyHours, drawTemp, showPrecip]);

  if (!drawTemp && bars.length === 0 && skyHours.length === 0) {
    return <div ref={ref} className="hidden" />;
  }

  const slotW = 1000 / HOURS_IN_WEEK;
  return (
    <div ref={ref} aria-hidden className="pointer-events-none absolute inset-0 z-[1]">
      {skyHours.length > 1 && (
        <SkyRibbon
          hours={skyHours}
          id={gradientId}
          style={{ bottom: SKY_BOTTOM_PX, height: SKY_HEIGHT_PX }}
        />
      )}
      {bars.length > 0 && (
        <svg
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          className="absolute left-0 w-full"
          style={{ bottom: PRECIP_BASE_PX, height: PRECIP_MAX_PX }}
        >
          {bars.map((h) => {
            const total = h.rainMm + h.snowMm;
            const height = barHeight(total) * 100;
            const rainH = (height * h.rainMm) / total;
            const x = h.x * 1000 - slotW + slotW * 0.25;
            const w = slotW * 0.5;
            const base = 100;
            // What fell is drawn steady; a forecast fades with its chance.
            const opacity = h.t > nowT ? (h.prob == null ? 0.28 : 0.08 + (0.27 * h.prob) / 100) : 0.35;
            return (
              <g
                key={h.t}
                opacity={opacity}
              >
                {rainH > 0 && <rect x={x} y={base - rainH} width={w} height={rainH} fill="var(--weather-rain)" />}
                {height - rainH > 0 && (
                  <rect x={x} y={base - height} width={w} height={height - rainH} fill="var(--text-muted)" />
                )}
              </g>
            );
          })}
        </svg>
      )}
      {drawTemp && (
        <svg
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
        >
          {segments.map((seg) => (
            <path
              key={seg[0].t}
              d={smoothPath(seg.map((h) => [h.x * 1000, h.y! * 100]))}
              fill="none"
              stroke="var(--accent)"
              strokeOpacity={0.45}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
      )}
      {nowPoint && (
        <>
          <span
            className="wx-breathe absolute h-[9px] w-[9px] rounded-full bg-[var(--accent)]"
            style={{ left: `${nowPoint.x * 100}%`, top: `${nowPoint.y * 100}%` }}
          />
          <span
            className="absolute h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--accent)] opacity-80"
            style={{
              left: `${nowPoint.x * 100}%`,
              top: `${nowPoint.y * 100}%`
            }}
          />
        </>
      )}
      {hover && (
        <>
          {hover.hour.y === hover.y && (
            <span
              className="absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--accent)]"
              style={{ left: `${hover.hour.x * 100}%`, top: `${hover.y * 100}%` }}
            />
          )}
          <span
            className="absolute z-[2] whitespace-nowrap rounded border border-[var(--border-strong)] bg-[var(--popover-bg)] px-[6px] py-[3px] text-[0.72rem] tabular-nums leading-none text-[var(--text)] shadow-md"
            style={{
              left: `${hover.hour.x * 100}%`,
              top: `${hover.y * 100}%`,
              transform: 'translate(-50%, calc(-100% - 8px))'
            }}
          >
            <span className="text-[var(--text-muted)]">{fmtHour(hover.hour.t)}</span>{' '}
            {tooltip(hover.hour, nowT, showTemp, showPrecip, showSky, temperatureUnit, precipitationUnit)}
          </span>
        </>
      )}
    </div>
  );
}

// A bar's height as a fraction of PRECIP_MAX_PX.
function barHeight(mm: number): number {
  return Math.sqrt(Math.min(mm, PRECIP_FULL_MM) / PRECIP_FULL_MM);
}

function tooltip(
  h: Hour,
  nowT: number,
  showTemp: boolean,
  showPrecip: boolean,
  showSky: boolean,
  tUnit: TemperatureUnit,
  pUnit: PrecipitationUnit
): string {
  const parts: string[] = [];
  if (showTemp && h.temp != null) parts.push(fmtTemp(h.temp, tUnit));
  if (showPrecip && h.rainMm + h.snowMm >= PRECIP_MIN_MM) {
    if (h.snowCm > 0) parts.push(`${fmtSnow(h.snowCm, pUnit)} snow`);
    if (h.rainMm >= PRECIP_MIN_MM) parts.push(fmtPrecip(h.rainMm, pUnit));
    if (h.t > nowT && h.prob != null) parts.push(`${Math.round(h.prob)}% chance`);
  }
  if (showSky && h.cloud != null) parts.push(`${Math.round(h.cloud)}% cloud`);
  return parts.join(', ');
}

export interface SkyHour {
  t: number;
  x: number; // 0..1 across the week
  cloud: number; // 0..100
  isDay: boolean;
}

// Daylight and cloud as one soft band. Two gradients with a stop per hour
// share the band: a warm wash for daylight, fading as cloud thickens, and a
// grey one for cloud, lighter at night. A clear night is the empty band, so
// the days read as a rhythm of light. `gain` scales both washes, for a band
// that stands alone rather than lying under event chips.
export function SkyRibbon({
  hours,
  id,
  style,
  gain = 1
}: {
  hours: SkyHour[];
  id: string;
  style: CSSProperties;
  gain?: number;
}) {
  const x0 = hours[0].x * 1000;
  const x1 = hours[hours.length - 1].x * 1000;
  const stops = (opacity: (hour: SkyHour) => number, color: string) =>
    hours.map((hour) => (
      <stop
        key={hour.t}
        offset={hour.x}
        stopColor={color}
        stopOpacity={Math.min(1, opacity(hour) * gain).toFixed(3)}
      />
    ));
  return (
    <svg
      viewBox="0 0 1000 1"
      preserveAspectRatio="none"
      className="absolute left-0 w-full"
      style={style}
    >
      <defs>
        <linearGradient id={`${id}-sun`} gradientUnits="userSpaceOnUse" x1="0" x2="1000" y1="0" y2="0">
          {stops((hr) => (hr.isDay ? 0.3 * (1 - hr.cloud / 100) : 0), 'var(--weather-sun)')}
        </linearGradient>
        <linearGradient id={`${id}-cloud`} gradientUnits="userSpaceOnUse" x1="0" x2="1000" y1="0" y2="0">
          {stops((hr) => (hr.cloud / 100) * (hr.isDay ? 0.45 : 0.25), 'var(--text-muted)')}
        </linearGradient>
      </defs>
      <rect x={x0} y={0} width={x1 - x0} height={1} fill={`url(#${id}-sun)`} />
      <rect x={x0} y={0} width={x1 - x0} height={1} fill={`url(#${id}-cloud)`} />
    </svg>
  );
}
